import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableItemId } from "../../../../components/tables/TableWidget";
import { receptionRoutes } from "../../../../router/routesConstants";
import { TableItemType } from "../../../../components/tables/tableContext/TableContext";
import CustomButton from "../../../../components/CustomButton";
import ActionMenu, {
  ActionMenuItem,
} from "../../../../components/actionMenu/ActionMenu";
import { useAppDispatch } from "../../../../redux/hooks";
import {
  getDeleteProduct,
  getReceiptsDataList,
  postSendReceivedProductLabeled,
  postSendReceivedProductToAnm,
} from "../../../../redux/api/receipt.api";
import ModalWrapper from "../../../../components/Modal/ModalWrapper";
import TextInput from "../../../../components/misc/TextInput";

function ReceivedProductsTableAction({
  itemId,
  crudData,
  status,
  receptionId,
}: {
  itemId: TableItemId;
  tableItemData: TableItemType[];
  crudData: {
    cantitate_trimisa?: number | string | null;
    cantitate?: number | string | null;
    receptionType?: number;
    numeProdusErp?: string;
  };
  status: number;
  receptionId?: string | number;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showLabeledModal, setShowLabeledModal] = useState(false);
  const [labeledQuantity, setLabeledQuantity] = useState<number | string>("");
  const [isSendingToAnm, setIsSendingToAnm] = useState(false);
  const [isSendingLabeled, setIsSendingLabeled] = useState(false);

  const finalizedReceptions = status >= 5;
  const availableQuantity = Math.max(0, Number(crudData?.cantitate) || 0);

  const editProduct = () => {
    navigate(
      `/${receptionRoutes.reception}/${receptionRoutes.receivedProductsEdit}?idProdus=${itemId}`,
    );
  };

  const deleteProduct = () => {
    dispatch(
      getDeleteProduct({
        idProdusReceptie: itemId,
      }),
    );
  };

  const refreshProducts = async () => {
    if (!receptionId) return;

    await dispatch(
      getReceiptsDataList({
        idFlux: receptionId,
        per_page: 9999,
      }),
    );
  };

  const sendToAnm = async () => {
    setIsSendingToAnm(true);
    try {
      await dispatch(
        postSendReceivedProductToAnm({ ...crudData, idProdusReceptie: itemId }),
      ).unwrap();
      await refreshProducts();
    } finally {
      setIsSendingToAnm(false);
    }
  };

  const closeLabeledModal = () => {
    setShowLabeledModal(false);
    setLabeledQuantity("");
  };

  const handleQuantityChange = (value: number | string | null) => {
    if (value === "" || value === null) {
      setLabeledQuantity("");
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      setLabeledQuantity(
        Math.min(Math.max(numericValue, 0), availableQuantity),
      );
    }
  };

  const quantity = Number(labeledQuantity);
  const isInvalidQuantity =
    !labeledQuantity ||
    !Number.isInteger(quantity) ||
    quantity <= 0 ||
    quantity > availableQuantity;

  const sendLabeled = async () => {
    if (isInvalidQuantity) return;

    setIsSendingLabeled(true);
    try {
      await dispatch(
        postSendReceivedProductLabeled({
          ...crudData,
          idProdusReceptie: itemId,
          cantitate: quantity,
        }),
      ).unwrap();
      await refreshProducts();
      closeLabeledModal();
    } finally {
      setIsSendingLabeled(false);
    }
  };

  const sendedQuantity = Number(crudData?.cantitate_trimisa) || 0;
  const isAipReception = Number(crudData?.receptionType) === 1;
  const items: ActionMenuItem[] = [
    {
      id: "send-to-anm",
      label: "Trimite ANM",
      icon: "LABEL",
      color: "info",
      tooltip: isSendingToAnm
        ? "Trimiterea produsului catre ANM este in curs."
        : "Trimite produsul receptionat catre ANM.",
      hidden: !isAipReception,
      loading: isSendingToAnm,
      disabled: isSendingToAnm,
      onClick: sendToAnm,
    },
    {
      id: "send-labeled",
      label: "Trimite etichetat",
      icon: "LABEL",
      color: "success",
      tooltip:
        availableQuantity < sendedQuantity
          ? "Cantitatea deja trimisa depaseste cantitatea disponibila."
          : "Alege cantitatea de produs care se trimite etichetat.",
      hidden: !isAipReception,
      disabled: availableQuantity < sendedQuantity,
      onClick: () => setShowLabeledModal(true),
    },
    {
      id: "edit-product",
      dividerBefore: isAipReception,
      label: "Editeaza",
      icon: "EDIT",
      tooltip: finalizedReceptions
        ? "Receptia este finalizata; produsul nu mai poate fi editat."
        : "Deschide editarea produsului receptionat.",
      disabled: finalizedReceptions,
      onClick: editProduct,
    },
    {
      id: "delete-product",
      label: "Sterge",
      icon: "DELETE",
      color: "error",
      tooltip: "Sterge produsul receptionat dupa confirmare.",
      withConfirmationModal: {
        modalTitle: "Stergere produs",
        modalSubtitle: "Esti sigur ca vrei sa stergi produsul?",
        modalLeftButtonColor: "inherit",
        modalRightButtonColor: "error",
        modalRightButtonOnClick: deleteProduct,
      },
    },
  ];

  return (
    <div>
      <ActionMenu
        items={items}
        minItemsForMenu={2}
        title={crudData.numeProdusErp}
        ariaLabel={`Actiuni pentru produsul receptionat ${itemId}`}
      />

      <ModalWrapper
        title="Trimite produs etichetat"
        show={showLabeledModal}
        handleClose={closeLabeledModal}>
        <TextInput
          label={`Cantitate (maxim ${availableQuantity - sendedQuantity})`}
          name="labeledQuantity"
          id="labeledQuantity"
          type="number"
          value={labeledQuantity}
          onChange={handleQuantityChange}
          onKeyDown={(event) => {
            if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
              event.preventDefault();
            }
          }}
          inputProps={{
            inputProps: {
              min: 1,
              max: availableQuantity - sendedQuantity,
              step: 1,
            },
          }}
          onlyPositiveNumbers
          autoFocus
        />
        <div className="flex justify-end">
          <CustomButton
            variant="contained"
            color="success"
            loading={isSendingLabeled}
            disabled={isInvalidQuantity || isSendingLabeled}
            onClick={sendLabeled}>
            Trimite
          </CustomButton>
        </div>
      </ModalWrapper>
    </div>
  );
}

export default ReceivedProductsTableAction;
